import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import Footer from './Footer.js';
import LoadingAnimation from './LoadingAnimation';
import { useConfig } from '../contexts/ConfigContext.js';
import { useAdminGuard } from '../hooks/useAdminGuard.js';
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from '../firebase';

const AdminConfig = () => {
  const navigate = useNavigate();
  const config = useConfig();
  const { currentUser, isReady } = useAdminGuard();

  const [dataReady, setDataReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Form state — mirrors the config fields
  const [currentSeason, setCurrentSeason] = useState('');
  const [adminEmailsText, setAdminEmailsText] = useState('');
  const [showSignUps, setShowSignUps] = useState(false);
  const [signUpText, setSignUpText] = useState('');
  const [returningPlayerFee, setReturningPlayerFee] = useState('');
  const [newPlayerFee, setNewPlayerFee] = useState('');
  const [showDraftBoard, setShowDraftBoard] = useState(false);
  const [showPlayoffBracket, setShowPlayoffBracket] = useState(false);
  const [gamesPerTeam, setGamesPerTeam] = useState('');
  const [locationsText, setLocationsText] = useState('');

  useEffect(() => {
    if (!isReady) return;

    const load = async () => {
      await loadConfig();
      setDataReady(true);
    };
    load();
  }, [isReady]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadConfig() {
    try {
      const configDoc = await getDoc(doc(db, 'config', 'app'));
      const data = configDoc.exists() ? configDoc.data() : config;

      setCurrentSeason(data.currentSeason ?? config.currentSeason);
      setAdminEmailsText((data.adminEmails ?? config.adminEmails).join('\n'));
      setShowSignUps(data.showSignUps ?? config.showSignUps);
      setSignUpText(data.signUpText ?? config.signUpText);
      setReturningPlayerFee(String(data.returningPlayerFee ?? config.returningPlayerFee));
      setNewPlayerFee(String(data.newPlayerFee ?? config.newPlayerFee));
      setShowDraftBoard(data.showDraftBoard ?? config.showDraftBoard);
      setShowPlayoffBracket(data.showPlayoffBracket ?? config.showPlayoffBracket);
      setGamesPerTeam(String(data.gamesPerTeam ?? config.gamesPerTeam));

      const locs = data.locations ?? config.locations;
      setLocationsText(locs.map(l => `${l.id}|${l.name}|${l.abbv}`).join('\n'));
    } catch (err) {
      console.error('Error loading config:', err);
    }
  }

  async function saveConfig() {
    setSaving(true);
    setSaveMessage('');
    try {
      const adminEmails = adminEmailsText.split('\n').map(e => e.trim()).filter(Boolean);
      const locations = locationsText.split('\n').map(line => {
        const [id, name, abbv] = line.split('|').map(s => s.trim());
        return { id, name, abbv };
      }).filter(l => l.id && l.name && l.abbv);

      const newConfig = {
        currentSeason: currentSeason.trim(),
        adminEmails,
        showSignUps,
        signUpText,
        showDraftBoard,
        returningPlayerFee: parseInt(returningPlayerFee, 10) || 0,
        newPlayerFee: parseInt(newPlayerFee, 10) || 0,
        showPlayoffBracket,
        gamesPerTeam: parseInt(gamesPerTeam, 10) || 0,
        locations
      };

      await setDoc(doc(db, 'config', 'app'), newConfig);
      await config.refreshConfig();
      setSaveMessage('Saved successfully!');
    } catch (err) {
      console.error('Error saving config:', err);
      setSaveMessage('Error saving. Check console for details.');
    } finally {
      setSaving(false);
    }
  }

  if (!dataReady || currentUser == null) {
    return <div className="Account"><LoadingAnimation /></div>;
  }

  return (
    <div>
      <div className="league-content">
        <div className="league-body">
          <p className='league-admin-back-button'><a className='league-link' href="/admin"><b>← Back to Admin Home</b></a></p>
          <p className="league-title-standard-secondary text-align-center">League Config</p>
          <p className="text-align-center" style={{ color: 'gray', marginBottom: '2rem' }}>
            Changes here take effect immediately — no code deploy needed.
          </p>

          <div className="league-table-container" style={{ maxWidth: 600, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

            <div>
              <label className="league-label"><strong>Current Season</strong></label>
              <p style={{ color: 'gray', fontSize: '0.85rem', margin: '0.25rem 0 0.5rem' }}>
                The active season number. Controls which season's data is shown by default.
              </p>
              <input
                className="form-control"
                type="text"
                value={currentSeason}
                onChange={e => setCurrentSeason(e.target.value)}
              />
            </div>

            <div>
              <label className="league-label"><strong>Show Sign-Ups</strong></label>
              <p style={{ color: 'gray', fontSize: '0.85rem', margin: '0.25rem 0 0.5rem' }}>
                Toggle the sign-up pages on or off for the public.
              </p>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showSignUps"
                  checked={showSignUps}
                  onChange={e => setShowSignUps(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="showSignUps">
                  {showSignUps ? 'Visible' : 'Hidden'}
                </label>
              </div>
            </div>

            <div>
              <label className="league-label"><strong>Sign-Up Text</strong></label>
              <p style={{ color: 'gray', fontSize: '0.85rem', margin: '0.25rem 0 0.5rem' }}>
                Body text shown on the homepage when sign-ups are visible.
              </p>
              <textarea
                className="form-control"
                rows={4}
                value={signUpText}
                onChange={e => setSignUpText(e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <div style={{ flex: 1 }}>
                <label className="league-label"><strong>Returning Player Fee ($)</strong></label>
                <input
                  className="form-control"
                  type="number"
                  value={returningPlayerFee}
                  onChange={e => setReturningPlayerFee(e.target.value)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label className="league-label"><strong>New Player Fee ($)</strong></label>
                <input
                  className="form-control"
                  type="number"
                  value={newPlayerFee}
                  onChange={e => setNewPlayerFee(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="league-label"><strong>Show Draft Board Button</strong></label>
              <p style={{ color: 'gray', fontSize: '0.85rem', margin: '0.25rem 0 0.5rem' }}>
                Show a "View Draft Board" button on the homepage during the draft period.
              </p>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showDraftBoard"
                  checked={showDraftBoard}
                  onChange={e => setShowDraftBoard(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="showDraftBoard">
                  {showDraftBoard ? 'Visible' : 'Hidden'}
                </label>
              </div>
            </div>

            <div>
              <label className="league-label"><strong>Show Playoff Bracket</strong></label>
              <p style={{ color: 'gray', fontSize: '0.85rem', margin: '0.25rem 0 0.5rem' }}>
                Toggle the playoff bracket section on or off.
              </p>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showPlayoffBracket"
                  checked={showPlayoffBracket}
                  onChange={e => setShowPlayoffBracket(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="showPlayoffBracket">
                  {showPlayoffBracket ? 'Visible' : 'Hidden'}
                </label>
              </div>
            </div>

            <div>
              <label className="league-label"><strong>Games Per Team (Current Season)</strong></label>
              <p style={{ color: 'gray', fontSize: '0.85rem', margin: '0.25rem 0 0.5rem' }}>
                Number of regular season games each team plays.
              </p>
              <input
                className="form-control"
                type="number"
                value={gamesPerTeam}
                onChange={e => setGamesPerTeam(e.target.value)}
              />
            </div>

            <div>
              <label className="league-label"><strong>Admin Emails</strong></label>
              <p style={{ color: 'gray', fontSize: '0.85rem', margin: '0.25rem 0 0.5rem' }}>
                One email per line. These accounts have access to admin pages.
              </p>
              <textarea
                className="form-control"
                rows={6}
                value={adminEmailsText}
                onChange={e => setAdminEmailsText(e.target.value)}
              />
            </div>

            <div>
              <label className="league-label"><strong>Court Locations</strong></label>
              <p style={{ color: 'gray', fontSize: '0.85rem', margin: '0.25rem 0 0.5rem' }}>
                One location per line in format: <code>id|Full Name|ABBV</code><br/>
                Example: <code>sbca|South Boston Catholic Academy|SBCA</code>
              </p>
              <textarea
                className="form-control"
                rows={6}
                value={locationsText}
                onChange={e => setLocationsText(e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <button
                className="btn btn-dark"
                onClick={saveConfig}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Config'}
              </button>
              {saveMessage && (
                <span style={{ color: saveMessage.startsWith('Error') ? 'red' : 'green' }}>
                  {saveMessage}
                </span>
              )}
            </div>

          </div>
        </div>
      </div>
      <Footer/>
    </div>
  );
};

export default AdminConfig;
